<script setup lang="ts">
import { computed, ref } from "vue";
import { useCrud } from "~/composables/use-crud";
import type { AdminUser } from "~~/schemas/admin-user";
import type { AdminRole } from "~~/schemas/admin-role";

const emit = defineEmits<{
  (e: "saved"): void;
}>();

const { t } = useI18n();
const toast = useToast();
const alert = useAlert();
const authStore = useAuthStore();
const { $api } = useNuxtApp();

// Password is write-only on create; it lives outside the wire AdminUser shape.
const password = ref("");

const defaultFormModel = (): AdminUser => ({
  id: "",
  email: "",
  fullName: "",
  roleKey: "",
  desc: "",
  createTime: 0,
  deleteTime: 0,
});

const {
  formModel,
  formErrors,
  errorMsg,
  saveLoading,
  reset: resetCrud,
  load,
  save,
} = useCrud<AdminUser>({
  endpoint: "/admin-users",
  defaultFormModel,
  serialize: (model) =>
    model.id
      ? {
          id: model.id,
          fullName: model.fullName,
          roleKey: model.roleKey,
          desc: model.desc,
        }
      : {
          email: model.email,
          password: password.value,
          fullName: model.fullName,
          roleKey: model.roleKey,
          desc: model.desc,
        },
});

function reset() {
  resetCrud();
  password.value = "";
}

const open = ref(false);

// Captured at load time so a self-edit role change is detected stably even
// after the user types in the form.
const originalRoleKey = ref("");

const roles = ref<AdminRole[]>([]);
const rolesLoaded = ref(false);

const roleOptions = computed(() =>
  roles.value.map((r) => ({ value: r.key, label: r.name })),
);

const isUpdate = computed(() => !!formModel.id);
const title = computed(() =>
  isUpdate.value ? t("adminUser.updateTitle") : t("adminUser.newTitle"),
);

async function ensureRolesLoaded() {
  if (rolesLoaded.value) return;
  try {
    const response: any = await $api("/roles", { params: { page: 1, pageSize: 50 } });
    roles.value = (response?.items || []) as AdminRole[];
    rolesLoaded.value = true;
  } catch (e: any) {
    toast.error({ title: e?.data?.message || t("adminUser.loadError") });
  }
}

async function openModal(id?: string) {
  reset();
  originalRoleKey.value = "";

  // Roles are needed regardless of mode — fetched once and cached.
  await ensureRolesLoaded();

  if (id) {
    try {
      await load(id);
      originalRoleKey.value = formModel.roleKey;
    } catch (e: any) {
      toast.error({ title: e?.data?.message || t("adminUser.loadError") });
      return;
    }
  }

  open.value = true;
}

function close() {
  open.value = false;
  reset();
  originalRoleKey.value = "";
}

async function submit() {
  // Self-edit role change: warn before submitting since changing your own
  // role can revoke permissions you currently rely on.
  if (
    isUpdate.value
    && formModel.id
    && formModel.id === authStore.user?.id
    && formModel.roleKey !== originalRoleKey.value
  ) {
    const ok = await alert.negative({
      title: t("adminUser.selfRoleChangeTitle"),
      description: t("adminUser.selfRoleChangeBody"),
      confirmLabel: t("adminUser.selfRoleChangeConfirm"),
      cancelLabel: t("action.cancel"),
    });
    if (!ok) return;
  }

  const isCreating = !isUpdate.value;
  try {
    await save();
    toast.success({
      title: t(isCreating ? "message.createSuccess" : "message.updateSuccess"),
    });
    emit("saved");
    close();
  } catch {
    if (errorMsg.value && Object.keys(formErrors.value).length === 0) {
      toast.error({ title: errorMsg.value });
    }
  }
}

defineExpose({
  open: openModal,
  close,
});
</script>

<template>
  <Modal
    :open="open"
    :title="title"
    size="md"
    @update:open="(v: boolean) => (open = v)"
    @close="close"
  >
    <!-- autocomplete="off" on the form root + unique field names below
         keep Chrome / Safari from autofilling the signed-in admin's
         credentials when the create form opens. -->
    <form
      class="flex flex-col gap-medium"
      autocomplete="off"
      data-testid="admin-user-form"
      @submit.prevent="submit"
    >
      <InlineMessage
        v-if="errorMsg && Object.keys(formErrors).length > 0"
        intent="negative"
        class="mb-2xsmall"
      >
        {{ errorMsg || t("adminUser.formError") }}
      </InlineMessage>

      <FormField
        v-model="formModel.fullName"
        :label="t('field.fullName')"
        :error="formErrors.fullName"
      />

      <FormField
        v-model="formModel.email"
        type="email"
        name="admin-create-email"
        autocomplete="off"
        :label="t('field.email')"
        :disabled="isUpdate"
        :error="formErrors.email"
        :help-text="isUpdate ? t('adminUser.emailLockedHelp') : undefined"
      />

      <FormField
        v-if="!isUpdate"
        v-model="password"
        type="password"
        name="admin-create-password"
        autocomplete="new-password"
        :label="t('field.password')"
        :error="formErrors.password"
      />

      <FormSelect
        v-model="formModel.roleKey"
        :options="roleOptions"
        :label="t('adminUser.columnRole')"
        :placeholder="t('adminUser.rolePlaceholder')"
        :empty-text="t('adminUser.roleEmpty')"
        :error="formErrors.roleKey"
      />

      <FormTextarea
        v-model="formModel.desc"
        :label="t('field.description')"
        :error="formErrors.desc"
      />
    </form>

    <template #actions>
      <Button variant="tertiary" size="small" @click="close">
        {{ t("action.cancel") }}
      </Button>
      <Button
        variant="primary"
        size="small"
        :disabled="saveLoading"
        data-testid="admin-user-form-save"
        @click="submit"
      >
        <ProgressCircle v-if="saveLoading" size="small" />
        {{ t("action.save") }}
      </Button>
    </template>
  </Modal>
</template>
